// InsightsDashboard v4 — clean rewrite with history tab
import React, { useMemo, useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import {
  LineChart, Line, AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
  PieChart, Pie, ReferenceLine,
} from 'recharts';
import { EMOJI_MAP, EMOTION_COLORS } from '../utils/constants';
import { computeDailySummary, computeExtendedSummary } from '../engine/metrics';
import { fetchRecentDailySummaries } from '../firebase';

const TOOLTIPS = {
  'Session Quality':    'How good was this session overall? 80+ means you were focused, calm and energetic. Below 50 means fatigue or distractions got in the way.',
  'Flow Time':          'Minutes where you were truly in the zone — deeply focused with a calm or engaged expression. This is your most productive time.',
  'Longest Flow':       'Your longest unbroken stretch of deep focus. Try to beat this in your next session.',
  'Avg Attention':      'How focused were you on average? Above 70 is solid. Below 40 means you were frequently distracted or fatigued.',
  'Peak Focus':         'You were sharpest at this hour today. Try to schedule your hardest tasks around this time.',
  'Lowest Focus':       'Your focus dipped most at this hour. A good time to take a break, reply to messages, or do low-effort work.',
  'Focus Streak':       'The longest you stayed continuously focused without drifting. Longer streaks = better deep work ability.',
  'Distractions':       'How many times you lost focus sharply. 0-2 is great. Above 5 suggests your environment or task may be interrupting you too often.',
  'Avg Recovery':       'After losing focus, how many minutes it took to get back on track. Shorter is better.',
  'Eye Strain Risk':    'How much of the session you were barely blinking. Above 50% means you should take more screen breaks.',
  'Fatigue Onset':      'The first moment signs of drowsiness appeared. If this keeps happening early in the day, you may need more sleep.',
  'Avg PERCLOS':        'How droopy your eyes were on average. Below 5% is fine, above 10% means you were quite tired.',
  'Avg Blink':          'How long each blink lasted. Quick blinks mean you are alert. Slow blinks above 250ms are a sign your brain is getting tired.',
  'Total Yawns':        'Yawns are a reliable sign your brain needs oxygen or rest. More than 5 means you were genuinely fatigued.',
  'Cognitive Load':     'How hard your brain was working. High load + high fatigue together is a warning to slow down.',
  'Emotion Stability':  'How consistent your mood was. High stability means you stayed calm. Low means frequent mood shifts.',
  'Frustration Index':  'A signal that you may have been struggling. Above 60 suggests something was genuinely frustrating you.',
  'Face Present':       'How long the camera could see your face.',
  'Morning Attention':  'Your average focus level before midday.',
  'Afternoon Attention':'Your average focus level after midday. If higher than morning, schedule lighter tasks in the morning.',
  'Days Tracked':       'Total days you have used Moodscope.',
  'Best Day':           'The day with your highest average attention score.',
  'Total Flow':         'Total flow minutes accumulated across all tracked days.',
  'Total Yawns (hist)': 'Total yawns detected across all tracked days.',
};

function InfoIcon({ text }) {
  const [rect, setRect] = useState(null);
  const ref = React.useRef(null);
  const show = () => { if (ref.current) setRect(ref.current.getBoundingClientRect()); };
  const hide = () => setRect(null);
  const popover = rect ? (() => {
    const W = 210;
    let left = rect.left + rect.width / 2 - W / 2;
    left = Math.max(8, Math.min(left, window.innerWidth - W - 8));
    const top = rect.top + window.scrollY - 8;
    return { left, top, W };
  })() : null;
  return (
    <>
      <span ref={ref} style={{ display:'inline-flex', verticalAlign:'middle', marginLeft:4, cursor:'default', flexShrink:0 }}
        onMouseEnter={show} onMouseLeave={hide}>
        <svg width="12" height="12" viewBox="0 0 13 13" fill="none" style={{ opacity:0.4 }}>
          <circle cx="6.5" cy="6.5" r="6" stroke="currentColor" strokeWidth="1.2"/>
          <text x="6.5" y="10" textAnchor="middle" fontSize="8" fill="currentColor" fontFamily="sans-serif" fontWeight="600">i</text>
        </svg>
      </span>
      {popover && typeof document !== 'undefined' && ReactDOM.createPortal(
        <div style={{
          position:'absolute', top: popover.top, left: popover.left,
          width: popover.W, transform:'translateY(-100%)',
          background:'#1f2937', color:'#f9fafb', fontSize:'0.65rem', lineHeight:1.6,
          borderRadius:8, padding:'9px 12px', boxShadow:'0 6px 24px rgba(0,0,0,0.25)',
          pointerEvents:'none', zIndex:9999, fontFamily:"'Nunito',sans-serif",
          whiteSpace:'normal', textAlign:'left',
        }}>
          {text}
        </div>,
        document.body
      )}
    </>
  );
}

function StatCard({ label, val, unit='', color='var(--accent)', subtext }) {
  return (
    <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:12, padding:'12px 14px', textAlign:'center' }}>
      <div style={{ fontFamily:"'Fraunces',serif", fontSize:'1.4rem', color, lineHeight:1, fontVariantNumeric:'tabular-nums' }}>
        {val}<span style={{ fontSize:'0.7rem', opacity:0.6 }}>{unit}</span>
      </div>
      <div style={{ fontSize:'0.56rem', color:'var(--muted)', marginTop:4, textTransform:'uppercase', letterSpacing:'0.08em', display:'flex', alignItems:'center', justifyContent:'center', gap:2 }}>
        {label}{TOOLTIPS[label] && <InfoIcon text={TOOLTIPS[label]} />}
      </div>
      {subtext && <div style={{ fontSize:'0.58rem', color:'var(--muted)', marginTop:3, opacity:0.7 }}>{subtext}</div>}
    </div>
  );
}

function Section({ title, icon, defaultOpen=true, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:14, overflow:'hidden' }}>
      <button onClick={() => setOpen(o => !o)} style={{
        width:'100%', display:'flex', alignItems:'center', justifyContent:'space-between',
        padding:'13px 18px', background:'transparent', border:'none', cursor:'pointer', fontFamily:"'Nunito',sans-serif",
      }}>
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          <span style={{ fontSize:'1rem' }}>{icon}</span>
          <span style={{ fontSize:'0.68rem', fontWeight:700, letterSpacing:'0.08em', textTransform:'uppercase', color:'var(--text)' }}>{title}</span>
        </div>
        <span style={{ fontSize:'0.7rem', color:'var(--muted)', transform: open ? 'rotate(180deg)' : 'none', transition:'transform 0.2s' }}>▾</span>
      </button>
      {open && <div style={{ padding:'4px 18px 18px' }}>{children}</div>}
    </div>
  );
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background:'var(--surface2)', border:'1px solid var(--border)', borderRadius:8, padding:'8px 12px', fontSize:'0.65rem' }}>
      <div style={{ color:'var(--muted)', marginBottom:4 }}>{label}</div>
      {payload.map(p => p.value != null && (
        <div key={p.dataKey} style={{ color: p.color || 'var(--text)' }}>
          {p.name}: {typeof p.value === 'number' ? p.value.toFixed(1) : p.value}
        </div>
      ))}
    </div>
  );
};

const fmtHour = h => h === null ? '—' : `${h}:00${h < 12 ? 'am' : 'pm'}`;
const fmtTime = ts => new Date(ts).toLocaleTimeString([], { hour:'2-digit', minute:'2-digit' });
const fmtDate = dateStr => new Date(dateStr).toLocaleDateString([], { weekday:'short', month:'short', day:'numeric' });

function ScoreRing({ score, label, color, size=80 }) {
  const r = (size - 10) / 2;
  const circ = 2 * Math.PI * r;
  const fill = circ * ((score || 0) / 100);
  return (
    <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:6 }}>
      <svg width={size} height={size} style={{ transform:'rotate(-90deg)' }}>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="var(--surface2)" strokeWidth={8} />
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={8}
          strokeDasharray={`${fill} ${circ}`} strokeLinecap="round"
          style={{ transition:'stroke-dasharray 0.8s ease' }} />
        <text x={size/2} y={size/2} textAnchor="middle" dominantBaseline="middle"
          style={{ transform:`rotate(90deg)`, transformOrigin:`${size/2}px ${size/2}px`,
            fontSize: size > 70 ? 18 : 13, fontFamily:'Fraunces,serif', fill:color, fontWeight:400 }}>
          {score || 0}
        </text>
      </svg>
      <div style={{ fontSize:'0.58rem', color:'var(--muted)', textTransform:'uppercase', letterSpacing:'0.07em', display:'flex', alignItems:'center', gap:2 }}>
        {label}{TOOLTIPS[label] && <InfoIcon text={TOOLTIPS[label]} />}
      </div>
    </div>
  );
}

export function InsightsDashboard({ snapshots, user }) {
  const summary  = useMemo(() => computeDailySummary(snapshots),   [snapshots]);
  const extended = useMemo(() => computeExtendedSummary(snapshots), [snapshots]);

  const chartData = useMemo(() =>
    snapshots.map(s => ({
      time:          fmtTime(s.timestamp),
      attention:     s.attentionScore,
      blinkRate:     s.blinkRate ? +s.blinkRate.toFixed(1) : null,
      perclos:       s.perclos ? +(s.perclos * 100).toFixed(1) : 0,
      blinkDuration: s.blinkDurationAvg || null,
    })), [snapshots]);

  const emotionData = useMemo(() => {
    if (!summary?.emotionDistribution) return [];
    return Object.entries(summary.emotionDistribution)
      .filter(([,v]) => v > 0).sort((a,b) => b[1]-a[1])
      .map(([e, pct]) => ({ name:`${EMOJI_MAP[e]||''} ${e}`, value:pct, color:EMOTION_COLORS[e]||'#888' }));
  }, [summary]);

  // History tab
  const [activeTab,   setActiveTab]   = useState('today');
  const [history,     setHistory]     = useState([]);
  const [historyLoad, setHistoryLoad] = useState(false);

  useEffect(() => {
    if (activeTab !== 'history' || !user) return;
    setHistoryLoad(true);
    fetchRecentDailySummaries(user.uid, 14)
      .then(data => { setHistory(data); setHistoryLoad(false); })
      .catch(() => setHistoryLoad(false));
  }, [activeTab, user]);

  if (!snapshots.length) {
    return (
      <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', minHeight:300, gap:12 }}>
        <div style={{ fontSize:'2rem' }}>📊</div>
        <div style={{ fontSize:'0.8rem', color:'var(--muted)' }}>No data yet — start tracking to see insights</div>
      </div>
    );
  }

  const qColor     = (extended?.sessionQuality   || 0) >= 70 ? 'var(--energetic)' : (extended?.sessionQuality   || 0) >= 40 ? '#f59e0b' : 'var(--angry)';
  const loadColor  = (extended?.cognitiveLoad    || 0) <= 30 ? 'var(--energetic)' : (extended?.cognitiveLoad    || 0) <= 60 ? '#f59e0b' : 'var(--angry)';
  const frustColor = (extended?.frustrationIndex || 0) <= 25 ? 'var(--energetic)' : (extended?.frustrationIndex || 0) <= 55 ? '#f59e0b' : 'var(--angry)';

  // History computed values
  const historyChartData = history.slice().reverse().map(d => ({
    date:      fmtDate(d.date),
    attention: d.avgAttentionScore ?? null,
    perclos:   d.avgPerclos != null ? +(d.avgPerclos * 100).toFixed(1) : null,
    flowMins:  d.flowMinutes ?? null,
    quality:   d.sessionQuality ?? null,
  }));
  const bestDay = history.length ? history.reduce((best, d) => (d.avgAttentionScore||0) > (best.avgAttentionScore||0) ? d : best, history[0]) : null;
  const avgHistoryAttention = history.length ? Math.round(history.reduce((s, d) => s + (d.avgAttentionScore||0), 0) / history.length) : null;

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:14, width:'100%' }}>

      {/* Tab switcher */}
      <div style={{ display:'flex', background:'var(--surface2)', borderRadius:8, padding:3, gap:2, alignSelf:'flex-start' }}>
        {[['today','📊 Today'], ['history','📅 History']].map(([tab, label]) => (
          <button key={tab} onClick={() => setActiveTab(tab)} style={{
            padding:'5px 16px', borderRadius:6, border:'none', cursor:'pointer',
            background: activeTab === tab ? 'var(--surface)' : 'transparent',
            color: activeTab === tab ? 'var(--text)' : 'var(--muted)',
            fontFamily:"'Nunito',sans-serif", fontSize:'0.7rem',
            fontWeight: activeTab === tab ? 600 : 400, transition:'all 0.15s',
          }}>{label}</button>
        ))}
      </div>

      {/* ── HISTORY TAB ──────────────────────────────────────────────────────── */}
      {activeTab === 'history' && (
        <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
          {historyLoad && (
            <div style={{ display:'flex', alignItems:'center', justifyContent:'center', minHeight:200, gap:12, color:'var(--muted)' }}>
              <span style={{ display:'inline-block', width:18, height:18, border:'2px solid var(--border)', borderTopColor:'var(--accent)', borderRadius:'50%', animation:'spin 0.7s linear infinite' }} />
              <span style={{ fontSize:'0.8rem', fontFamily:"'Nunito',sans-serif" }}>Loading history…</span>
            </div>
          )}
          {!historyLoad && history.length === 0 && (
            <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', minHeight:200, gap:12 }}>
              <div style={{ fontSize:'2rem' }}>📅</div>
              <div style={{ fontSize:'0.8rem', color:'var(--muted)' }}>No history yet — come back after a few more sessions</div>
            </div>
          )}
          {!historyLoad && history.length > 0 && (
            <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
              <Section title="Your Trends" icon="📈" defaultOpen={true}>
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(120px,1fr))', gap:10, marginBottom:16 }}>
                  <StatCard label="Days Tracked" val={history.length} unit=" days" color="var(--accent)" />
                  <StatCard label="Avg Attention" val={avgHistoryAttention ?? '—'} unit="/100"
                    color={avgHistoryAttention && avgHistoryAttention >= 70 ? 'var(--energetic)' : '#f59e0b'} />
                  <StatCard label="Best Day" val={bestDay ? fmtDate(bestDay.date) : '—'} unit=""
                    color="var(--accent)" subtext={bestDay ? `${bestDay.avgAttentionScore}/100` : ''} />
                  <StatCard label="Total Flow" val={history.reduce((s,d) => s + (d.flowMinutes||0), 0)} unit=" min" color="var(--accent2)" />
                </div>
                <div style={{ fontSize:'0.6rem', color:'var(--muted)', marginBottom:8, textTransform:'uppercase', letterSpacing:'0.08em' }}>Attention over {history.length} days</div>
                <ResponsiveContainer width="100%" height={150}>
                  <AreaChart data={historyChartData} margin={{ top:4, right:4, left:-20, bottom:0 }}>
                    <defs>
                      <linearGradient id="histAttnGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%"  stopColor="var(--accent)" stopOpacity={0.25}/>
                        <stop offset="95%" stopColor="var(--accent)" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false}/>
                    <XAxis dataKey="date" tick={{ fontSize:9, fill:'var(--muted)' }} tickLine={false}/>
                    <YAxis domain={[0,100]} tick={{ fontSize:9, fill:'var(--muted)' }} tickLine={false}/>
                    <Tooltip content={<CustomTooltip/>}/>
                    <ReferenceLine y={70} stroke="var(--accent)" strokeDasharray="4 3" strokeOpacity={0.4}
                      label={{ value:'Target', position:'right', fontSize:8, fill:'var(--accent)' }}/>
                    <Area type="monotone" dataKey="attention" name="Avg Attention" stroke="var(--accent)" fill="url(#histAttnGrad)" strokeWidth={2} dot={{ r:3, fill:'var(--accent)' }}/>
                  </AreaChart>
                </ResponsiveContainer>
              </Section>

              <Section title="Session Quality & Flow" icon="🎯" defaultOpen={true}>
                <ResponsiveContainer width="100%" height={150}>
                  <BarChart data={historyChartData} margin={{ top:4, right:4, left:-20, bottom:0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false}/>
                    <XAxis dataKey="date" tick={{ fontSize:9, fill:'var(--muted)' }} tickLine={false}/>
                    <YAxis tick={{ fontSize:9, fill:'var(--muted)' }} tickLine={false}/>
                    <Tooltip content={<CustomTooltip/>}/>
                    <Bar dataKey="quality"  name="Session Quality" radius={[4,4,0,0]} fill="var(--accent)"  opacity={0.85}/>
                    <Bar dataKey="flowMins" name="Flow Minutes"    radius={[4,4,0,0]} fill="var(--accent2)" opacity={0.7}/>
                  </BarChart>
                </ResponsiveContainer>
              </Section>

              <Section title="Fatigue Trend" icon="😴" defaultOpen={true}>
                <div style={{ fontSize:'0.6rem', color:'var(--muted)', marginBottom:8 }}>Average PERCLOS % per day — above 10% indicates fatigue</div>
                <ResponsiveContainer width="100%" height={130}>
                  <AreaChart data={historyChartData} margin={{ top:4, right:4, left:-20, bottom:0 }}>
                    <defs>
                      <linearGradient id="histFatGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%"  stopColor="#8b5cf6" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false}/>
                    <XAxis dataKey="date" tick={{ fontSize:9, fill:'var(--muted)' }} tickLine={false}/>
                    <YAxis tick={{ fontSize:9, fill:'var(--muted)' }} tickLine={false}/>
                    <Tooltip content={<CustomTooltip/>}/>
                    <ReferenceLine y={10} stroke="#8b5cf6" strokeDasharray="4 3" strokeOpacity={0.5}
                      label={{ value:'Fatigue threshold', position:'right', fontSize:8, fill:'#8b5cf6' }}/>
                    <Area type="monotone" dataKey="perclos" name="Avg PERCLOS %" stroke="#8b5cf6" fill="url(#histFatGrad)" strokeWidth={2} dot={{ r:3, fill:'#8b5cf6' }}/>
                  </AreaChart>
                </ResponsiveContainer>
              </Section>

              <Section title="Day by Day" icon="🗓" defaultOpen={false}>
                <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                  {history.map(d => (
                    <div key={d.date} style={{ display:'grid', gridTemplateColumns:'80px 1fr 50px 60px 50px', gap:8, alignItems:'center', padding:'8px 10px', background:'var(--surface2)', borderRadius:8, fontSize:'0.65rem' }}>
                      <div style={{ color:'var(--muted)', fontFamily:"'Nunito',sans-serif" }}>{fmtDate(d.date)}</div>
                      <div style={{ height:4, background:'var(--border)', borderRadius:2, overflow:'hidden' }}>
                        <div style={{ height:'100%', width:`${d.avgAttentionScore||0}%`, background:'var(--accent)', borderRadius:2 }}/>
                      </div>
                      <div style={{ textAlign:'center', fontFamily:"'Fraunces',serif", color:'var(--accent)', fontVariantNumeric:'tabular-nums' }}>{d.avgAttentionScore ?? '—'}</div>
                      <div style={{ textAlign:'center', color:'var(--muted)', fontVariantNumeric:'tabular-nums' }}>{d.flowMinutes ?? 0}m flow</div>
                      <div style={{ textAlign:'center', color:(d.sessionQuality||0) >= 70 ? 'var(--energetic)' : '#f59e0b', fontVariantNumeric:'tabular-nums' }}>{d.sessionQuality ?? '—'} Q</div>
                    </div>
                  ))}
                </div>
              </Section>
            </div>
          )}
        </div>
      )}

      {/* ── TODAY TAB ────────────────────────────────────────────────────────── */}
      {activeTab === 'today' && (
        <div style={{ display:'flex', flexDirection:'column', gap:14 }}>

          <Section title="Session Summary" icon="🎯" defaultOpen={true}>
            {extended && (
              <div style={{ display:'flex', gap:20, justifyContent:'center', marginBottom:18, flexWrap:'wrap' }}>
                <ScoreRing score={extended.sessionQuality}   label="Session Quality"   color={qColor}     size={90} />
                <ScoreRing score={extended.cognitiveLoad}    label="Cognitive Load"    color={loadColor}  size={90} />
                <ScoreRing score={extended.emotionStability} label="Emotion Stability" color="var(--accent)" size={90} />
                <ScoreRing score={extended.frustrationIndex} label="Frustration Index" color={frustColor} size={90} />
              </div>
            )}
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(120px,1fr))', gap:10 }}>
              {summary && (
                <>
                  <StatCard label="Avg Attention" val={summary.avgAttentionScore} unit="/100"
                    color={summary.avgAttentionScore >= 70 ? 'var(--energetic)' : summary.avgAttentionScore >= 40 ? '#fbbf24' : 'var(--angry)'} />
                  <StatCard label="Face Present" val={summary.totalFacePresentMin} unit="min" color="var(--muted)" />
                </>
              )}
              {extended && (
                <>
                  <StatCard label="Flow Time"    val={extended.flowMinutes}       unit="min" color="var(--accent)" />
                  <StatCard label="Longest Flow" val={extended.longestFlowStreak} unit="min" color="var(--accent2)" />
                  <StatCard label="Distractions" val={extended.distractions} unit=""
                    color={extended.distractions <= 2 ? 'var(--energetic)' : extended.distractions <= 5 ? '#f59e0b' : 'var(--angry)'} />
                  <StatCard label="Avg Recovery" val={extended.avgRecoveryMins ?? '—'} unit={extended.avgRecoveryMins ? 'min' : ''} color="var(--muted)" />
                </>
              )}
            </div>
          </Section>

          <Section title="Focus & Attention" icon="🧠" defaultOpen={true}>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(110px,1fr))', gap:10, marginBottom:16 }}>
              {summary && (
                <>
                  <StatCard label="Peak Focus"   val={fmtHour(summary.peakFocusHour)}   unit="" color="var(--accent)" />
                  <StatCard label="Lowest Focus" val={fmtHour(summary.lowestFocusHour)} unit="" color="var(--angry)" />
                  <StatCard label="Focus Streak" val={summary.longestFocusStreakMin}     unit="min" color="var(--accent2)" />
                </>
              )}
            </div>
            <div style={{ fontSize:'0.6rem', color:'var(--muted)', marginBottom:8, textTransform:'uppercase', letterSpacing:'0.08em' }}>Attention over time</div>
            <ResponsiveContainer width="100%" height={140}>
              <AreaChart data={chartData} margin={{ top:4, right:4, left:-20, bottom:0 }}>
                <defs>
                  <linearGradient id="attnGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor="var(--accent)" stopOpacity={0.25}/>
                    <stop offset="95%" stopColor="var(--accent)" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false}/>
                <XAxis dataKey="time" tick={{ fontSize:9, fill:'var(--muted)' }} tickLine={false}/>
                <YAxis domain={[0,100]} tick={{ fontSize:9, fill:'var(--muted)' }} tickLine={false}/>
                <Tooltip content={<CustomTooltip/>}/>
                <ReferenceLine y={75} stroke="var(--accent)" strokeDasharray="4 3" strokeOpacity={0.4}
                  label={{ value:'Flow', position:'right', fontSize:8, fill:'var(--accent)' }}/>
                <ReferenceLine y={40} stroke="var(--angry)" strokeDasharray="4 3" strokeOpacity={0.3}/>
                <Area type="monotone" dataKey="attention" name="Attention" stroke="var(--accent)" fill="url(#attnGrad)" strokeWidth={2} dot={false}/>
              </AreaChart>
            </ResponsiveContainer>
            {extended?.flowTimeline?.length > 1 && (
              <>
                <div style={{ fontSize:'0.6rem', color:'var(--muted)', margin:'14px 0 8px', textTransform:'uppercase', letterSpacing:'0.08em' }}>Flow state timeline</div>
                <ResponsiveContainer width="100%" height={100}>
                  <AreaChart data={extended.flowTimeline} margin={{ top:4, right:4, left:-20, bottom:0 }}>
                    <defs>
                      <linearGradient id="flowGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%"  stopColor="#0cb8a0" stopOpacity={0.4}/>
                        <stop offset="95%" stopColor="#0cb8a0" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false}/>
                    <XAxis dataKey="time" tick={{ fontSize:9, fill:'var(--muted)' }} tickLine={false}/>
                    <YAxis domain={[0,100]} hide/>
                    <Tooltip content={<CustomTooltip/>}/>
                    <Area type="monotone" dataKey="attention" name="Attention" stroke="var(--border)" fill="transparent" strokeWidth={1} dot={false}/>
                    <Area type="monotone" dataKey="flow" name="Flow" stroke="#0cb8a0" fill="url(#flowGrad)" strokeWidth={2.5} dot={false} connectNulls={false}/>
                  </AreaChart>
                </ResponsiveContainer>
                <div style={{ fontSize:'0.58rem', color:'var(--muted)', marginTop:4, opacity:0.7 }}>Green = flow state · Grey = below threshold</div>
              </>
            )}
          </Section>

          <Section title="Fatigue & Eye Health" icon="👁" defaultOpen={true}>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(110px,1fr))', gap:10, marginBottom:16 }}>
              {summary && (
                <>
                  <StatCard label="Avg PERCLOS" val={(summary.avgPerclos*100).toFixed(1)} unit="%"
                    color={summary.avgPerclos > 0.10 ? 'var(--sleepy)' : 'var(--muted)'} />
                  <StatCard label="Avg Blink"   val={summary.avgBlinkDuration} unit="ms" color="#f0d060" />
                  <StatCard label="Total Yawns" val={summary.yawnCount} unit=""
                    color={summary.yawnCount > 5 ? '#8b5cf6' : 'var(--muted)'} />
                </>
              )}
              {extended && (
                <>
                  <StatCard label="Eye Strain Risk" val={extended.eyeStrainIndex} unit="%"
                    color={extended.eyeStrainIndex > 50 ? 'var(--angry)' : extended.eyeStrainIndex > 25 ? '#f59e0b' : 'var(--energetic)'} />
                  <StatCard label="Fatigue Onset" val={extended.fatigueOnsetTime ?? 'None'} unit=""
                    color={extended.fatigueOnsetTime ? 'var(--sleepy)' : 'var(--energetic)'}
                    subtext={extended.fatigueOnsetTime ? 'first fatigue signal' : 'no fatigue detected'} />
                </>
              )}
            </div>
            {extended?.fatigueChart?.length > 1 && (
              <>
                <div style={{ fontSize:'0.6rem', color:'var(--muted)', marginBottom:8, textTransform:'uppercase', letterSpacing:'0.08em' }}>Fatigue curve (PERCLOS %)</div>
                <ResponsiveContainer width="100%" height={130}>
                  <AreaChart data={extended.fatigueChart} margin={{ top:4, right:4, left:-20, bottom:0 }}>
                    <defs>
                      <linearGradient id="fatGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%"  stopColor="#8b5cf6" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false}/>
                    <XAxis dataKey="time" tick={{ fontSize:9, fill:'var(--muted)' }} tickLine={false}/>
                    <YAxis tick={{ fontSize:9, fill:'var(--muted)' }} tickLine={false}/>
                    <Tooltip content={<CustomTooltip/>}/>
                    <ReferenceLine y={10} stroke="#8b5cf6" strokeDasharray="4 3" strokeOpacity={0.5}
                      label={{ value:'Fatigue threshold', position:'right', fontSize:8, fill:'#8b5cf6' }}/>
                    <Area type="monotone" dataKey="perclos" name="PERCLOS %" stroke="#8b5cf6" fill="url(#fatGrad)" strokeWidth={2} dot={false}/>
                  </AreaChart>
                </ResponsiveContainer>
              </>
            )}
            <div style={{ fontSize:'0.6rem', color:'var(--muted)', margin:'14px 0 8px', textTransform:'uppercase', letterSpacing:'0.08em' }}>Blink rate over time</div>
            <ResponsiveContainer width="100%" height={110}>
              <LineChart data={chartData} margin={{ top:4, right:4, left:-20, bottom:0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false}/>
                <XAxis dataKey="time" tick={{ fontSize:9, fill:'var(--muted)' }} tickLine={false}/>
                <YAxis tick={{ fontSize:9, fill:'var(--muted)' }} tickLine={false}/>
                <Tooltip content={<CustomTooltip/>}/>
                <ReferenceLine y={10} stroke="var(--angry)"     strokeDasharray="4 3" strokeOpacity={0.4}/>
                <ReferenceLine y={20} stroke="var(--energetic)" strokeDasharray="4 3" strokeOpacity={0.3}/>
                <Line type="monotone" dataKey="blinkRate" name="Blink /min" stroke="#f0d060" strokeWidth={2} dot={false}/>
              </LineChart>
            </ResponsiveContainer>
          </Section>

          <Section title="Emotion & Stress" icon="😌" defaultOpen={true}>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(280px,1fr))', gap:14 }}>
              {emotionData.length > 0 && (
                <div>
                  <div style={{ fontSize:'0.6rem', color:'var(--muted)', marginBottom:8, textTransform:'uppercase', letterSpacing:'0.08em' }}>Emotion distribution</div>
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(200px,1fr))', gap:12, alignItems:'center' }}>
                    <ResponsiveContainer width="100%" height={150}>
                      <PieChart>
                        <Pie data={emotionData} cx="50%" cy="50%" innerRadius={35} outerRadius={60} paddingAngle={3} dataKey="value">
                          {emotionData.map((e,i) => <Cell key={i} fill={e.color}/>)}
                        </Pie>
                        <Tooltip formatter={(v) => `${v}%`}/>
                      </PieChart>
                    </ResponsiveContainer>
                    <div style={{ display:'flex', flexDirection:'column', gap:5 }}>
                      {emotionData.slice(0,5).map(e => (
                        <div key={e.name} style={{ display:'flex', alignItems:'center', gap:8, fontSize:'0.65rem' }}>
                          <div style={{ width:8, height:8, borderRadius:'50%', background:e.color, flexShrink:0 }}/>
                          <span style={{ flex:1, color:'var(--text)' }}>{e.name}</span>
                          <span style={{ color:'var(--muted)' }}>{e.value}%</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              {extended && (
                <div>
                  <div style={{ fontSize:'0.6rem', color:'var(--muted)', marginBottom:10, textTransform:'uppercase', letterSpacing:'0.08em' }}>Stress indicators</div>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                    <StatCard label="Cognitive Load"    val={extended.cognitiveLoad}    unit="/100" color={loadColor} />
                    <StatCard label="Emotion Stability" val={extended.emotionStability} unit="/100" color="var(--accent)" />
                    <StatCard label="Frustration Index" val={extended.frustrationIndex} unit="/100" color={frustColor} />
                  </div>
                </div>
              )}
            </div>
          </Section>

          {extended && (extended.morningAttn !== null || extended.afternoonAttn !== null) && (
            <Section title="Time Patterns" icon="📅" defaultOpen={false}>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(120px,1fr))', gap:10, marginBottom:14 }}>
                {extended.morningAttn !== null && (
                  <StatCard label="Morning Attention" val={extended.morningAttn} unit="/100"
                    color={extended.morningAttn >= 70 ? 'var(--energetic)' : '#f59e0b'} />
                )}
                {extended.afternoonAttn !== null && (
                  <StatCard label="Afternoon Attention" val={extended.afternoonAttn} unit="/100"
                    color={extended.afternoonAttn >= 70 ? 'var(--energetic)' : '#f59e0b'} />
                )}
              </div>
              {extended.morningAttn !== null && extended.afternoonAttn !== null && (
                <ResponsiveContainer width="100%" height={100}>
                  <BarChart data={[
                    { period:'Morning',   score: extended.morningAttn },
                    { period:'Afternoon', score: extended.afternoonAttn },
                  ]} margin={{ top:4, right:4, left:-20, bottom:0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false}/>
                    <XAxis dataKey="period" tick={{ fontSize:10, fill:'var(--muted)' }} tickLine={false}/>
                    <YAxis domain={[0,100]} tick={{ fontSize:9, fill:'var(--muted)' }} tickLine={false}/>
                    <Tooltip content={<CustomTooltip/>}/>
                    <Bar dataKey="score" name="Avg Attention" radius={[6,6,0,0]}>
                      <Cell fill="var(--accent)"/>
                      <Cell fill="var(--accent2)"/>
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Section>
          )}

        </div>
      )}

    </div>
  );
}
